import { collection, doc, setDoc, getDoc, updateDoc, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from './firestore';
import { KashierTransaction, PaymentResult } from '../types/payment';
import { getCurrentMillis } from '../utils/dateUtils';

const TRANSACTIONS_COLLECTION = 'SubscriptionsTransactions';

class TransactionService {
  /**
   * Create transaction document in Firestore
   */
  async createTransaction(
    transactionData: Partial<KashierTransaction>,
    paymentResult: PaymentResult
  ): Promise<KashierTransaction> {
    try {

      const transactionId = paymentResult.transactionId || this.generateTransactionId();
      const now = getCurrentMillis();

      const fullTransactionData: KashierTransaction = {
        // Transaction identifiers
        transactionId,
        orderId: transactionData.orderId || '',
        orderReference: transactionData.orderReference || '',
        merchantOrderId: transactionData.merchantOrderId || '',
        trxReferenceNumber: transactionData.trxReferenceNumber || transactionId,
        
        // Payment details
        transactionAmount: transactionData.transactionAmount || '0',
        transactionCurrency: 'EGP',
        paymentStatus: paymentResult.paymentStatus || 'PENDING',
        
        // Card information
        cardBrand: transactionData.cardBrand || '',
        cardDataToken: transactionData.cardDataToken || '',
        maskedCard: transactionData.maskedCard || '',
        
        // Subscription details
        subscriptionPlanId: transactionData.subscriptionPlanId || '',
        subscriptionPlanName: transactionData.subscriptionPlanName || '',
        subscriptionPlanDescription: transactionData.subscriptionPlanDescription || '',
        subscriptionPlanPrice: transactionData.subscriptionPlanPrice || '0',
        subscriptionPlanDuration: transactionData.subscriptionPlanDuration || '1',
        subscriptionPlanDurationType: transactionData.subscriptionPlanDurationType || 'monthly',
        
        // User information
        userId: transactionData.userId || '',
        userName: transactionData.userName || '',
        userEmail: transactionData.userEmail || '',
        userPhone: transactionData.userPhone || '',
        
        // System fields
        createdAt: now.toString(),
        updatedAt: now.toString(),
        createdBy: transactionData.userId || '',
        language: transactionData.language || 'en',
        mode: transactionData.mode || 'test',
        signature: transactionData.signature || ''
      };

      // Create document in Firestore
      const transactionRef = doc(db, TRANSACTIONS_COLLECTION, transactionId);
      await setDoc(transactionRef, fullTransactionData);

      return fullTransactionData;

    } catch (error) {
      console.error('❌ TransactionService: Error creating transaction document:', error);
      throw new Error('Failed to create transaction document');
    }
  }

  /**
   * Update transaction status
   */
  async updateTransactionStatus(
    transactionId: string, 
    status: 'SUCCESS' | 'FAILED' | 'PENDING' | 'CANCELLED',
    additionalData?: Partial<KashierTransaction>
  ): Promise<void> {
    try {

      const transactionRef = doc(db, TRANSACTIONS_COLLECTION, transactionId);
      const updateData: any = {
        paymentStatus: status,
        updatedAt: getCurrentMillis().toString()
      };

      // Add additional data if provided
      if (additionalData) {
        Object.assign(updateData, additionalData);
      }

      await updateDoc(transactionRef, updateData);


    } catch (error) {
      console.error('❌ TransactionService: Error updating transaction status:', error);
      throw new Error('Failed to update transaction status');
    }
  }

  /**
   * Get transaction by ID
   */
  async getTransaction(transactionId: string): Promise<KashierTransaction | null> {
    try {

      const transactionRef = doc(db, TRANSACTIONS_COLLECTION, transactionId);
      const transactionDoc = await getDoc(transactionRef);

      if (transactionDoc.exists()) {
        const transactionData = transactionDoc.data() as KashierTransaction;
        return transactionData;
      } else {
        return null;
      }

    } catch (error) {
      console.error('❌ TransactionService: Error getting transaction:', error);
      throw new Error('Failed to get transaction');
    }
  }

  /**
   * Get transactions by user ID
   */
  async getUserTransactions(userId: string, limitCount: number = 10): Promise<KashierTransaction[]> {
    try {

      const transactionsRef = collection(db, TRANSACTIONS_COLLECTION);
      const q = query(
        transactionsRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const transactions: KashierTransaction[] = [];

      querySnapshot.forEach((doc) => {
        transactions.push(doc.data() as KashierTransaction);
      });

      return transactions;

    } catch (error) {
      console.error('❌ TransactionService: Error getting user transactions:', error);
      throw new Error('Failed to get user transactions');
    }
  }

  /**
   * Get transactions by subscription plan
   */
  async getPlanTransactions(planId: string, limitCount: number = 50): Promise<KashierTransaction[]> {
    try {

      const transactionsRef = collection(db, TRANSACTIONS_COLLECTION);
      const q = query(
        transactionsRef,
        where('subscriptionPlanId', '==', planId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const transactions: KashierTransaction[] = [];

      querySnapshot.forEach((doc) => {
        transactions.push(doc.data() as KashierTransaction);
      });

      return transactions;

    } catch (error) {
      console.error('❌ TransactionService: Error getting plan transactions:', error);
      throw new Error('Failed to get plan transactions');
    }
  }

  /**
   * Get successful transactions
   */
  async getSuccessfulTransactions(limitCount: number = 100): Promise<KashierTransaction[]> {
    try {

      const transactionsRef = collection(db, TRANSACTIONS_COLLECTION);
      const q = query(
        transactionsRef,
        where('paymentStatus', '==', 'SUCCESS'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const transactions: KashierTransaction[] = [];

      querySnapshot.forEach((doc) => {
        transactions.push(doc.data() as KashierTransaction);
      });

      return transactions;

    } catch (error) {
      console.error('❌ TransactionService: Error getting successful transactions:', error);
      throw new Error('Failed to get successful transactions');
    }
  }

  /**
   * Get transaction statistics
   */
  async getTransactionStats(userId?: string): Promise<{
    totalTransactions: number;
    successfulTransactions: number;
    failedTransactions: number;
    totalAmount: number;
    averageAmount: number;
  }> {
    try {

      const transactionsRef = collection(db, TRANSACTIONS_COLLECTION);
      let q = query(transactionsRef);

      if (userId) {
        q = query(transactionsRef, where('userId', '==', userId));
      }

      const querySnapshot = await getDocs(q);
      const transactions: KashierTransaction[] = [];

      querySnapshot.forEach((doc) => {
        transactions.push(doc.data() as KashierTransaction);
      });

      const stats = {
        totalTransactions: transactions.length,
        successfulTransactions: transactions.filter(t => t.paymentStatus === 'SUCCESS').length,
        failedTransactions: transactions.filter(t => t.paymentStatus === 'FAILED').length,
        totalAmount: transactions
          .filter(t => t.paymentStatus === 'SUCCESS')
          .reduce((sum, t) => sum + parseFloat(t.transactionAmount), 0),
        averageAmount: 0
      };

      stats.averageAmount = stats.successfulTransactions > 0 
        ? stats.totalAmount / stats.successfulTransactions 
        : 0;

      return stats;

    } catch (error) {
      console.error('❌ TransactionService: Error getting transaction statistics:', error);
      throw new Error('Failed to get transaction statistics');
    }
  }

  /**
   * Generate transaction ID
   */
  private generateTransactionId(): string {
    return `TX-${Date.now()}${Math.floor(Math.random() * 1000)}`;
  }

  /**
   * Create transaction from payment result
   */
  async createTransactionFromPaymentResult(
    paymentResult: PaymentResult,
    subscriptionData: any,
    userData: any
  ): Promise<KashierTransaction> {
    try {

      const transactionData: Partial<KashierTransaction> = {
        orderId: paymentResult.orderId || '',
        orderReference: `MORAFAA-${Date.now()}`,
        merchantOrderId: subscriptionData.id || '',
        subscriptionPlanId: subscriptionData.planId || '',
        subscriptionPlanName: subscriptionData.planName || '',
        subscriptionPlanDescription: subscriptionData.planDescription || '',
        subscriptionPlanPrice: subscriptionData.price?.toString() || '0',
        subscriptionPlanDuration: subscriptionData.duration || '1',
        subscriptionPlanDurationType: subscriptionData.durationType || 'monthly',
        userId: userData.id || '',
        userName: userData.displayName || userData.email || '',
        userEmail: userData.email || '',
        userPhone: userData.phoneNumber || '',
        language: 'en',
        mode: process.env.NODE_ENV === 'production' ? 'live' : 'test',
        signature: this.generateSignature(paymentResult)
      };

      return await this.createTransaction(transactionData, paymentResult);

    } catch (error) {
      console.error('❌ TransactionService: Error creating transaction from payment result:', error);
      throw error;
    }
  }

  /**
   * Generate signature for transaction
   */
  private generateSignature(data: any): string {
    // In a real implementation, you would use a proper signature generation method
    // This is a simplified version using browser-compatible crypto
    const dataString = JSON.stringify(data);
    return this.simpleHash(dataString);
  }

  /**
   * Simple hash function for browser compatibility
   */
  private simpleHash(str: string): string {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
  }

  /**
   * Get transaction by ID
   */
  async getTransactionById(transactionId: string): Promise<KashierTransaction | null> {
    try {
      const q = query(
        collection(db, TRANSACTIONS_COLLECTION),
        where('transactionId', '==', transactionId),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return doc.data() as KashierTransaction;
    } catch (error) {
      console.error('Error getting transaction by ID:', error);
      throw error;
    }
  }
}

export const transactionService = new TransactionService();
